export type ApId = string | { [key: string]: any } | null;
export type ApMap<T = any> = { [key: string]: T };

export interface ApDevice {
  _id?: ApId;
  mac?: string;
  name?: string;
  location?: string;
  country?: string;
  state?: string;
  city?: string;
  domain?: string;
  organization?: string;
  msp?: string;
  model?: string;
  serialNumber?: string;
  firmware?: string;
  deviceMetaInfo?: ApDeviceMetaInfo;
  provisionedConfig?: ApProvisionedConfig;
  machineId?: string;
  interfaces?: any[];
  upTime?: number | string;
  status?: string;
  pending?: boolean;
  orgId?: ApId;
  user?: any;
  baseLicense?: any;
  addOnLicense?: any[];
  prtgId?: ApId;
  prtgSensorId?: string;
  alert?: ApAlertConfig;
  alertId?: ApId;
  wnetwork?: ApId;
  qos?: ApId;
  "4glte"?: ApId;
  team?: ApId;
  templateTrigger?: any;
  templateConfig?: ApTemplateConfig;
  tprofile?: ApId;
  revisionId?: string;
  port?: any;
  statistics?: ApStatistics;
  lastSeen?: string | Date;
  orderdate?: string | Date;
  activatedAt?: string | Date;
  reset403At?: string | Date;
  createdAt?: string | Date;
}

export interface ApDeviceMetaInfo {
  deviceModel?: string;
  deviceType?: string;
}

export interface ApDeviceModelOption {
  name: string;
  value: string;
  count: number;
  checked: boolean;
}

export interface ApProvisionedConfig {
  nameServer?: string;
  address?: string;
  gatewayIp?: string;
  netmask?: string;
}

export interface ApAlertConfig {
  device?: boolean;
  team?: boolean;
  link1?: boolean;
  link2?: boolean;
  link3?: boolean;
  emails?: string[];
  escalateConfig?: number;
  glteConfig?: number;
  xdrConfig?: ApAlertXdrConfig;
  additionalEmails?: string[];
}

export interface ApAlertXdrConfig {
  alertOnWarning?: boolean;
  alertOnRecovering?: boolean;
  alertOnSafe?: boolean;
}

export interface ApTemplateConfig {
  qos?: any;
  wnetwork?: any;
  monitor?: any;
  "4glte"?: any;
  team?: any;
  autologin?: any;
  jump?: any;
}

export interface ApStatistics {
  firmware?: ApMap;
  system?: ApMap;
  teams?: ApTeamsStatistics;
  "4glte"?: ApGlteStatistics;
  bridges?: ApMap;
  wifi?: ApMap;
  memory?: ApMap;
  logging?: ApMap;
  sipwatch?: ApSipwatchStatistics;
  flowdoscheck?: ApFlowdoscheckStatistics;
  ups?: ApMap;
  avg_load?: any[];
  statusType?: string;
  mac?: string;
  machineId?: string;
  upTime?: number;
  cpuCount?: number;
  nicCount?: number;
  arch?: string;
  connection_count?: string | number;
  connection_max?: string | number;
  dhcp_leases?: any[];
  dhcp6_leases?: any[];
  ether_ports?: ApEtherPorts;
  vlan_info?: ApVlanInfo;
  assoc_list?: any[];
  timezone?: string;
  interfaces?: any[];
  wireguard?: any[];
  dpc_ha?: any[];
  monitor_stream?: ApMonitorStream;
  authlog?: any[];
  rrt_midlay?: ApRrtMidlay;
}

export interface ApTeamsStatistics {
  team1?: {
    links?: any[] | ApMap;
  };
  [teamKey: string]: any;
}

export interface ApGlteStatistics {
  apn?: string;
  imei?: string;
  ccid?: string;
  imsi?: string;
  iccid?: string;
  pdp?: string;
  mtu?: string | number;
  ip?: string;
  gateway?: string;
  subnet?: string;
  dns1?: string;
  dns2?: string;
  phone_number?: string;
  sim_status?: string;
  operator?: string;
  data_status?: string;
}

export interface ApSipwatchStatistics {
  common?: ApMap;
  sipwatch?: ApMap;
  callerid?: ApMap;
  watchlist?: any[];
  callerid_map?: any[];
  siptrack?: any[];
  callerid_hist?: any[];
}

export interface ApFlowdoscheckStatistics {
  state?: ApMap;
  config?: ApMap;
  statistics?: ApMap;
  state_hist?: any[];
  events?: any[];
  statusType?: string;
  timestamp?: number;
  xdr_block_log?: ApMap;
  xdr_dnswatch?: any[];
  xdr_config?: ApFlowdoscheckXdrConfig;
  ashield_rule?: any[];
  ddos_ipset?: ApMap;
  DOS_quarantined?: any[];
  Attack_5m?: ApMap;
  Attack_detail?: any[];
  SPAM_5m?: ApMap;
  SPAM_detail?: any[];
  GeoIP_5m?: ApMap;
  GeoIP_detail?: any[];
  DOS_5m?: ApMap;
  DNSWatch_5m?: ApMap;
  DNSWatch_detail?: any[];
  topall?: ApXdrTop;
}

export interface ApFlowdoscheckXdrConfig {
  common?: ApMap;
  threat_feed?: any[];
  dnswatch?: ApMap;
  geoip?: ApMap;
}

export interface ApXdrTop {
  dnswatch?: ApXdrTopItem;
  attack?: ApXdrTopItem;
  geoip?: ApXdrTopItem;
  spam?: ApXdrTopItem;
  dos?: ApXdrTopItem;
}

export interface ApXdrTopItem {
  t?: number;
  state?: string;
  alertlevel?: string;
  items?: any[];
}

export interface ApEtherPorts {
  ether_ports?: any[];
  model?: string;
  mode?: string;
  version?: number;
}

export interface ApVlanInfo {
  lan_vlan?: any[];
  wan_vlan?: any[];
  lan_ports?: any[];
  lan2_ports?: any[];
  lan3_ports?: any[];
  brouter?: number;
  mode?: string;
  lan_mac?: string;
  wan_mac?: string;
  wan2_mac?: string;
}

export interface ApMonitorStream {
  summary?: any[];
  window_minutes?: number;
  generated_at?: number;
  monitorstream_5min?: any[];
}

export type ApRrtMidlay = ApRrtMidlayVhub | ApMap<ApRrtMidlayDevice>;

export interface ApRrtMidlayCommonLink {
  name?: string;
  state?: string;
  weight?: number;
  rx_weight?: number;
  timeout?: number;
  interval?: number;
  last_rx_ms?: number;
  tx_packets?: number;
  rx_packets?: number;
  tx_bytes?: number;
  rx_bytes?: number;
  rtt_avg_ms?: number;
  rtt_p90_ms?: number;
  rtt_mad_ms?: number;
  rtt_jitter_ms?: number;
  mos?: string;
  action?: string;
}

export interface ApRrtMidlayDeviceLink extends ApRrtMidlayCommonLink {
  lba_enabled?: boolean;
  effective_weight?: number;
  iface?: string;
  server?: string;
  flapcheck_period_ms?: number;
  ka_sent?: number;
  ka_acked?: number;
  pre_fec_loss_pct?: number;
  post_fec_loss_pct?: number;
  post_fec_jitter_ms?: number;
}

export interface ApRrtMidlayVhubLink extends ApRrtMidlayCommonLink {
  transmit?: boolean;
  src?: string;
}

export interface ApRrtMidlayCommonQfecLane {
  lane?: number;
  k?: number;
  n?: number;
  mode_str?: string;
  loss_pct?: number;
  decode_latency_avg_ms?: number;
  decode_latency_p90_ms?: number;
  jitter_ms?: number;
  mos?: string;
  tx_packets?: number;
  tx_bytes?: number;
  rx_packets?: number;
  rx_bytes?: number;
}

export interface ApRrtMidlayVhubQfecLane extends ApRrtMidlayCommonQfecLane {
  mode?: number;
  tblmax?: number;
}

export interface ApRrtMidlayTeamConfig {
  version?: string;
  provisioned?: string;
  remote_addr?: string;
  dpc_gateway?: string;
  dpc_ip?: string;
  encrypt?: string;
  mtu?: string;
  keepalive?: string;
  ulay_mode?: string;
  iface?: string;
  sdwan_link?: string;
  maxpacketsize?: string;
  link_names?: string;
  route_tables?: string;
  weights?: string;
  timeouts?: string;
  flapcheck_period?: string;
  fec?: string;
  distribution?: string;
  transport?: string;
  rseq_hold_ms?: string;
  rseq_thin_acks?: string;
  maxblocks?: string;
  fec_group_hold?: string;
  fec_group_hold_depth?: string;
  [fecQueueConfigKey: string]: string | undefined;
}

export interface ApRrtMidlayDevice {
  status?: string;
  mode?: string;
  name?: string;
  state?: string;
  version?: string;
  uptime_sec?: number;
  total_uptime_sec?: number;
  start_count?: number;
  active_link?: string;
  active_index?: number;
  num_links?: number;
  fec_enabled?: boolean;
  fec_auto?: boolean;
  fec_ratio?: string;
  fec_flush_ms?: number;
  pps?: number;
  links?: ApRrtMidlayDeviceLink[];
  wan_id?: string;
  link_names?: string;
  fec_dec?: ApRrtMidlayFecDecoder;
  qfec_lanes?: ApRrtMidlayCommonQfecLane[];
  distribution?: string;
  provisioned?: string;
  team_config?: ApRrtMidlayTeamConfig;
}

export interface ApRrtMidlayFecDecoder {
  loss_pct?: number;
  decode_latency_avg_ms?: number;
  decode_latency_p90_ms?: number;
  jitter_ms?: number;
  mos?: string;
}

export interface ApRrtMidlayVhubDpc {
  name?: string;
  enabled?: number;
  listen_addr?: string;
  forward_addr?: string;
  dpc_ip?: string;
  tunnel_ip?: string;
  ip4table?: string;
  dpc_gateway?: string;
  mtu?: number;
  maxpacketsize?: number;
  fec?: string;
  fec_timeout?: number;
  sdwan_link?: string;
  pool?: any;
  auth_required?: number;
  dmz_ipset?: string;
  wan_ipset?: string;
  server_url?: string;
  qos_rate?: string;
}

export interface ApRrtMidlayVhubPeer {
  mac?: string;
  server_url?: string;
  server_name?: string;
  local?: string;
  hostname?: string;
  team?: string;
  client_model?: string;
  dpc_allowed?: string;
  encrypt?: string;
  dpc_ip?: string;
  wan_id?: string;
  wan_subnet?: string;
  remote_addr?: string;
  dpc_gateway?: string;
  mtu?: string;
  keepalive?: string;
  vce_provision?: ApRrtMidlayTeamConfig;
  connected_sec?: number;
  links?: ApRrtMidlayVhubLink[];
  live_dpc_ip?: string;
  route_tables?: string;
  fec?: string;
  fec_k?: number;
  fec_n?: number;
  qfec_lanes?: ApRrtMidlayVhubQfecLane[];
  tx_packets?: number;
  rx_packets?: number;
  tx_bytes?: number;
  rx_bytes?: number;
  distribution?: string;
}

export interface ApRrtMidlayVhub {
  dpcs?: ApRrtMidlayVhubDpc[];
  peers?: ApRrtMidlayVhubPeer[];
}

export function getApDeviceModel(device: Partial<ApDevice> | any): string {
  const model = String(device && device.deviceMetaInfo && device.deviceMetaInfo.deviceModel || device && device.model || '').trim();
  return model || 'Unknown';
}

export function buildApDeviceModelOptions(
  devices: Array<Partial<ApDevice> | any>,
  selectedModels: { [model: string]: boolean } = {}
): ApDeviceModelOption[] {
  const counts = (devices || []).reduce((acc, device) => {
    const model = getApDeviceModel(device);
    acc[model] = (acc[model] || 0) + 1;
    return acc;
  }, {} as { [model: string]: number });

  Object.keys(selectedModels)
    .filter(model => selectedModels[model] && counts[model] === undefined)
    .forEach(model => counts[model] = 0);

  return Object.keys(counts)
    .sort((a, b) => a.localeCompare(b))
    .map(model => ({
      name: model,
      value: model,
      count: counts[model],
      checked: !!selectedModels[model]
    }));
}

export function filterApDevicesByModel(
  devices: Array<Partial<ApDevice> | any>,
  selectedModels: { [model: string]: boolean } = {}
): ApDevice[] {
  const activeModels = Object.keys(selectedModels).filter(model => selectedModels[model]);
  if (!activeModels.length) {
    return [...(devices || [])] as ApDevice[];
  }

  return (devices || []).filter(device => activeModels.indexOf(getApDeviceModel(device)) > -1) as ApDevice[];
}
